import type { TShapeGeometry, TShapeKind, TShapePreset } from '@/types/shapes'

const kappa = 0.5522847498

export const shapeGeometries: Record<TShapeKind, TShapeGeometry> = {
  rectangle: {
    type: 'polygon',
    points: [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 1, y: 1 },
      { x: 0, y: 1 },
    ],
  },
  ellipse: {
    type: 'curve',
    commands: [
      { type: 'move', x: 1, y: 0.5 },
      { type: 'cubic', x1: 1, y1: 0.5 + kappa / 2, x2: 0.5 + kappa / 2, y2: 1, x: 0.5, y: 1 },
      { type: 'cubic', x1: 0.5 - kappa / 2, y1: 1, x2: 0, y2: 0.5 + kappa / 2, x: 0, y: 0.5 },
      { type: 'cubic', x1: 0, y1: 0.5 - kappa / 2, x2: 0.5 - kappa / 2, y2: 0, x: 0.5, y: 0 },
      { type: 'cubic', x1: 0.5 + kappa / 2, y1: 0, x2: 1, y2: 0.5 - kappa / 2, x: 1, y: 0.5 },
      { type: 'close' },
    ],
  },
  triangle: {
    type: 'polygon',
    points: [
      { x: 0.5, y: 0 },
      { x: 1, y: 1 },
      { x: 0, y: 1 },
    ],
  },
  rightTriangle: {
    type: 'polygon',
    points: [
      { x: 0, y: 0 },
      { x: 1, y: 1 },
      { x: 0, y: 1 },
    ],
  },
  diamond: {
    type: 'polygon',
    points: [
      { x: 0.5, y: 0 },
      { x: 1, y: 0.5 },
      { x: 0.5, y: 1 },
      { x: 0, y: 0.5 },
    ],
  },
  pentagon: {
    type: 'polygon',
    points: [
      { x: 0.5, y: 0 },
      { x: 1, y: 0.382 },
      { x: 0.809, y: 1 },
      { x: 0.191, y: 1 },
      { x: 0, y: 0.382 },
    ],
  },
  hexagon: {
    type: 'polygon',
    points: [
      { x: 0.25, y: 0 },
      { x: 0.75, y: 0 },
      { x: 1, y: 0.5 },
      { x: 0.75, y: 1 },
      { x: 0.25, y: 1 },
      { x: 0, y: 0.5 },
    ],
  },
  octagon: {
    type: 'polygon',
    points: [
      { x: 0.2929, y: 0 },
      { x: 0.7071, y: 0 },
      { x: 1, y: 0.2929 },
      { x: 1, y: 0.7071 },
      { x: 0.7071, y: 1 },
      { x: 0.2929, y: 1 },
      { x: 0, y: 0.7071 },
      { x: 0, y: 0.2929 },
    ],
  },
  star: {
    type: 'polygon',
    points: [
      { x: 0.5, y: 0 },
      { x: 0.6298, y: 0.365 },
      { x: 1, y: 0.382 },
      { x: 0.71, y: 0.6245 },
      { x: 0.809, y: 1 },
      { x: 0.5, y: 0.785 },
      { x: 0.191, y: 1 },
      { x: 0.29, y: 0.6245 },
      { x: 0, y: 0.382 },
      { x: 0.3702, y: 0.365 },
    ],
  },
  plus: {
    type: 'polygon',
    points: [
      { x: 1 / 3, y: 0 },
      { x: 2 / 3, y: 0 },
      { x: 2 / 3, y: 1 / 3 },
      { x: 1, y: 1 / 3 },
      { x: 1, y: 2 / 3 },
      { x: 2 / 3, y: 2 / 3 },
      { x: 2 / 3, y: 1 },
      { x: 1 / 3, y: 1 },
      { x: 1 / 3, y: 2 / 3 },
      { x: 0, y: 2 / 3 },
      { x: 0, y: 1 / 3 },
      { x: 1 / 3, y: 1 / 3 },
    ],
  },
  blockArrow: {
    type: 'polygon',
    points: [
      { x: 0, y: 0.3 },
      { x: 0.6, y: 0.3 },
      { x: 0.6, y: 0 },
      { x: 1, y: 0.5 },
      { x: 0.6, y: 1 },
      { x: 0.6, y: 0.7 },
      { x: 0, y: 0.7 },
    ],
  },
  chevron: {
    type: 'polygon',
    points: [
      { x: 0, y: 0 },
      { x: 0.65, y: 0 },
      { x: 1, y: 0.5 },
      { x: 0.65, y: 1 },
      { x: 0, y: 1 },
      { x: 0.35, y: 0.5 },
    ],
  },
  speechBubble: {
    type: 'polygon',
    points: [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 1, y: 0.75 },
      { x: 0.45, y: 0.75 },
      { x: 0.2, y: 1 },
      { x: 0.25, y: 0.75 },
      { x: 0, y: 0.75 },
    ],
  },
  heart: {
    type: 'curve',
    commands: [
      { type: 'move', x: 0.5, y: 1 },
      { type: 'cubic', x1: 0.2, y1: 0.8, x2: 0, y2: 0.55, x: 0, y: 0.3 },
      { type: 'cubic', x1: 0, y1: 0.12, x2: 0.13, y2: 0, x: 0.28, y: 0 },
      { type: 'cubic', x1: 0.38, y1: 0, x2: 0.46, y2: 0.06, x: 0.5, y: 0.16 },
      { type: 'cubic', x1: 0.54, y1: 0.06, x2: 0.62, y2: 0, x: 0.72, y: 0 },
      { type: 'cubic', x1: 0.87, y1: 0, x2: 1, y2: 0.12, x: 1, y: 0.3 },
      { type: 'cubic', x1: 1, y1: 0.55, x2: 0.8, y2: 0.8, x: 0.5, y: 1 },
      { type: 'close' },
    ],
  },
  line: {
    type: 'open',
    points: [
      { x: 0, y: 0.5 },
      { x: 1, y: 0.5 },
    ],
  },
  bentArrow: {
    type: 'open',
    points: [
      { x: 0, y: 0 },
      { x: 0, y: 1 },
      { x: 1, y: 1 },
    ],
  },
  elbowArrow: {
    type: 'open',
    points: [
      { x: 0, y: 0 },
      { x: 0, y: 0.5 },
      { x: 1, y: 0.5 },
      { x: 1, y: 1 },
    ],
  },
}

export const shapePresets: TShapePreset[] = [
  { id: 'square', label: 'Square', tags: ['rectangle', 'box'], kind: 'rectangle', ratio: 1 },
  {
    id: 'rounded-square',
    label: 'Rounded square',
    tags: ['rectangle', 'box', 'rounded'],
    kind: 'rectangle',
    ratio: 1,
    radiusRatio: 0.15,
  },
  { id: 'rectangle', label: 'Rectangle', tags: ['box', 'banner'], kind: 'rectangle', ratio: 1.6 },
  { id: 'circle', label: 'Circle', tags: ['ellipse', 'oval', 'round'], kind: 'ellipse', ratio: 1 },
  { id: 'triangle', label: 'Triangle', tags: ['pyramid'], kind: 'triangle', ratio: 1.155 },
  { id: 'right-triangle', label: 'Right triangle', tags: ['triangle', 'corner'], kind: 'rightTriangle', ratio: 1 },
  { id: 'diamond', label: 'Diamond', tags: ['rhombus'], kind: 'diamond', ratio: 1 },
  { id: 'pentagon', label: 'Pentagon', tags: ['polygon'], kind: 'pentagon', ratio: 1.051 },
  { id: 'hexagon', label: 'Hexagon', tags: ['polygon', 'honeycomb'], kind: 'hexagon', ratio: 1.155 },
  { id: 'octagon', label: 'Octagon', tags: ['polygon', 'stop'], kind: 'octagon', ratio: 1 },
  { id: 'star', label: 'Star', tags: ['favorite', 'rating'], kind: 'star', ratio: 1.051 },
  { id: 'plus', label: 'Plus', tags: ['cross', 'add'], kind: 'plus', ratio: 1 },
  { id: 'heart', label: 'Heart', tags: ['love', 'like'], kind: 'heart', ratio: 1.1 },
  {
    id: 'speech-bubble',
    label: 'Speech bubble',
    tags: ['callout', 'comment', 'quote'],
    kind: 'speechBubble',
    ratio: 1.3,
  },
  { id: 'block-arrow', label: 'Block arrow', tags: ['arrow', 'right', 'direction'], kind: 'blockArrow', ratio: 1.6 },
  { id: 'chevron', label: 'Chevron', tags: ['arrow', 'step', 'process'], kind: 'chevron', ratio: 0.8 },
  { id: 'line', label: 'Line', tags: ['divider', 'separator'], kind: 'line', ratio: 1 },
  { id: 'arrow', label: 'Arrow', tags: ['line', 'pointer', 'direction'], kind: 'line', ratio: 1, lineEnd: 'arrow' },
  {
    id: 'double-arrow',
    label: 'Double arrow',
    tags: ['line', 'arrow', 'both'],
    kind: 'line',
    ratio: 1,
    lineStart: 'arrow',
    lineEnd: 'arrow',
  },
  {
    id: 'bent-arrow',
    label: 'Bent arrow',
    tags: ['arrow', 'connector', 'corner', 'flow'],
    kind: 'bentArrow',
    ratio: 1.2,
    radiusRatio: 0.1,
    lineEnd: 'arrow',
  },
  {
    id: 'elbow-arrow',
    label: 'Elbow arrow',
    tags: ['arrow', 'connector', 'pipe', 'step', 'flow'],
    kind: 'elbowArrow',
    ratio: 1.5,
    radiusRatio: 0.1,
    lineEnd: 'arrow',
  },
]
